/**
 * First-move hint memory (card 9tynEjL4q4DT).
 *
 * The `.tab-hint` one-liners (thread / life / notebook) name the one action
 * that starts each tab. A hint is remembered as SEEN PER COUPLE: the flags
 * live in the couple room (`hints_seen`, served by GET/POST /api/hints), so a
 * hint dismissed on one device is not drawn on any other device, and the flag
 * outlives the browser profile. localStorage is no longer a store — the
 * legacy `pear.hints.seen` key is deleted on the first successful hydrate so
 * a stale second source can never resurface a hint.
 *
 * The cache is synchronous and hydrated from the API once per session load;
 * until the room answers, every hint reads as seen, so a dismissed hint never
 * flashes back while the hydrate is in flight. `markHintSeen` updates the
 * cache optimistically and posts fire-and-forget — it never throws and never
 * blocks a render. There is no offline queue and no retry: if the POST fails
 * the failure is logged once and the hint is unseen again for the rest of the
 * session.
 *
 * State is read through `useHintSeen`, a `useSyncExternalStore` subscription,
 * so marking a hint seen inside a panel (which is where the action happens)
 * re-renders the screen that draws the hint.
 */
import { useSyncExternalStore } from 'react';
import { api, getToken } from './api.ts';
import { isHintKey, type HintKey } from '../../../shared/types';

export type { HintKey };

/** The retired localStorage key — removed once, on the first successful
 *  hydrate, and never read or written again. */
const LEGACY_STORE_KEY = 'pear.hints.seen';

/** null until the room has answered for the current session token. */
let seen: Set<HintKey> | null = null;
let hydratedToken: string | null = null;
let hydrateFor: string | null = null;
let hydratePromise: Promise<void> | null = null;
const listeners = new Set<() => void>();
const loggedFailures = new Set<HintKey>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** Hydrate the cache from GET /api/hints — once per session load (one attempt
 *  per session token; a sign-in that happens after module load re-triggers
 *  it). Never throws: a hint that cannot reach its room must not break the
 *  screen. */
function hydrate(): Promise<void> {
  const token = getToken();
  if (!token) return Promise.resolve();
  if (seen && hydratedToken === token) return Promise.resolve();
  if (!hydratePromise || hydrateFor !== token) {
    const t = token;
    hydrateFor = t;
    hydratePromise = (async () => {
      try {
        const res = await api<{ seen?: unknown }>('/api/hints');
        const list = Array.isArray(res?.seen) ? res.seen : [];
        const fromRoom = new Set(list.filter(isHintKey));
        // A different token is a different room: start clean. The same room:
        // merge, never replace, so an optimistic markHintSeen that landed
        // while the GET was in flight survives the hydrate.
        if (hydratedToken !== t) seen = null;
        seen = new Set([...(seen ?? []), ...fromRoom]);
        hydratedToken = t;
        // The room is now the only store — kill the legacy key for good.
        try {
          localStorage.removeItem(LEGACY_STORE_KEY);
        } catch {
          /* storage unavailable — nothing to clean up */
        }
        emit();
      } catch (e) {
        // One attempt per session load: no retry loop. Hints simply stay
        // hidden (unhydrated reads as seen) for the rest of this session.
        console.warn('[hints] GET /api/hints failed; hints stay hidden this session', e);
        hydrateFor = null;
      }
    })();
  }
  return hydratePromise;
}

// Kick the hydrate off at session load; subscribe() re-kicks it so a sign-in
// without a reload still hydrates.
void hydrate();

/** Has this couple already done the thing this hint names? */
export function isHintSeen(key: HintKey): boolean {
  return seen === null ? true : seen.has(key);
}

/** Record that the hint's action has happened. Idempotent, silent on failure,
 *  and never blocks: the local cache updates now, the room is posted to
 *  fire-and-forget. */
export function markHintSeen(key: HintKey): void {
  if (!seen) seen = new Set();
  if (seen.has(key)) return;
  seen.add(key);
  emit();
  void api<{ seen?: unknown }>('/api/hints', { body: { key } })
    .then((res) => {
      // The room is the authority on "seen": adopt its union, so a hint seen
      // on the partner's phone is not drawn here either.
      if (seen && Array.isArray(res?.seen)) {
        const next = new Set([...seen, ...res.seen.filter(isHintKey)]);
        if (next.size !== seen.size) {
          seen = next;
          emit();
        }
      }
    })
    .catch(() => {
      // No queue, no retry: log the failure once and put the hint back to
      // unseen for the rest of this session.
      if (seen?.delete(key)) emit();
      if (!loggedFailures.has(key)) {
        loggedFailures.add(key);
        console.warn(`[hints] POST /api/hints failed for '${key}'; the hint stays unseen this session`);
      }
    });
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  void hydrate();
  return () => {
    listeners.delete(onStoreChange);
  };
}

/** `true` once this couple has already done the thing the hint names. */
export function useHintSeen(key: HintKey): boolean {
  return useSyncExternalStore(
    subscribe,
    () => isHintSeen(key),
    () => false,
  );
}
